import {
  MorphemeTilePresentationProvider,
  useMorphemeTilePresentation,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import type { VariantProps } from "@-/com.npmjs.cva";
import { useCallback } from "react";
import { cn } from "@-/com.npmjs.cn";
import type { MorphemeGlyphTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import {
  type GlyphStyle,
  type MorphemeGlyphTileWordFront,
  MorphemeGlyphTileContent,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs._components.content";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import {
  MORPHEME_TILE_DELAY_LONG_PRESS_MS,
  morphemeGlyphTileVariants,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.variants";

export type MorphemeGlyphTileStage = NonNullable<
  VariantProps<typeof morphemeGlyphTileVariants>["stage"]
>;

export type MorphemeGlyphTileCompletion = NonNullable<
  VariantProps<typeof morphemeGlyphTileVariants>["completion"]
>;

export type MorphemeGlyphTileUiProps = {
  className?: string;
  completion?: MorphemeGlyphTileCompletion;
  /**
   * Word tiles only. Curriculum word levels pass `"pinyin"` so the tile shows the readings that
   * make up the word instead of the written glyphs.
   */
  front?: MorphemeGlyphTileWordFront;
  /**
   * Pre-computed glyph size. When set, the tile renders with this `fontSize`/`lineHeight` on
   * the first commit instead of measuring itself via `onLayout`. Use from virtualized grids
   * where the tile size is derivable up front — skips the per-tile race that can leave the
   * glyph stuck at `opacity-0` if `onLayout` never fires.
   */
  glyphStyle?: GlyphStyle;
  graphemeGlyph?: GraphemeGlyph;
  isLoading?: boolean;
  /**
   * Whether this tile's grapheme (or word) card is NOT yet version-complete (`version >= 0`) in its
   * grapheme file.
   * When the device setting `fadeMissingMnemonicEnabled` is on, such tiles render at `opacity-25`
   * so unfinished mnemonics stand out in authoring grids. The caller resolves it from a BATCHED
   * per-grid `completed`-by-grapheme read (never a per-tile query — that would storm the JS thread);
   * left `undefined` the tile is never faded. The setting itself is read here so callers only pass
   * the completion fact, not the toggle.
   */
  isMissingMnemonic?: boolean;
  /**
   * Render the tile in a selected state (used by multi-select / long-press selection). Overlays a
   * solid primary ring on top of the stage/completion border so the selection reads clearly
   * regardless of the tile's SRS background.
   */
  isSelected?: boolean;
  morphemeGlyphTileSlice: MorphemeGlyphTileSlice;
  /** Fired on long press — used to enter multi-select mode from a morpheme tile. */
  onLongPress?: () => void;
  onPress?: (morphemeGlyphTileSlice: MorphemeGlyphTileSlice) => void;
  /** Show the grapheme pronunciation card's reading above its glyph. */
  showPrimaryPinyin?: boolean;
  /**
   * SRS stage background from {@link morphemeGlyphTileVariants}. Defaults to `"unadded"`.
   * Pair with `completion` in authoring views to overlay a completion border, e.g.
   * `stage="familiar" completion="needs-description"`.
   */
  stage?: MorphemeGlyphTileStage;
  /** Render the glyph in the theme foreground color, ignoring tone colors. */
  uncolored?: boolean;
};

function getMorphemeTileTestID(morpheme: string) {
  return `morpheme-tile-${Array.from(
    morpheme,
    (character) => character.codePointAt(0)?.toString(16) ?? "unknown",
  ).join("-")}`;
}

function MorphemeGlyphTileUiContent({
  className,
  completion,
  front,
  glyphStyle,
  graphemeGlyph,
  isLoading,
  isMissingMnemonic = false,
  isSelected = false,
  morphemeGlyphTileSlice,
  onLongPress,
  onPress,
  showPrimaryPinyin,
  stage = "unadded",
  uncolored,
}: MorphemeGlyphTileUiProps) {
  const { fadeMissingMnemonicEnabled } = useMorphemeTilePresentation();
  const handlePress = useCallback(() => {
    onPress?.(morphemeGlyphTileSlice);
  }, [morphemeGlyphTileSlice, onPress]);

  const resolvedClassName = cn(
    morphemeGlyphTileVariants({
      completion: isLoading ? "loading" : completion,
      stage,
    }),
    // The selected ring sits above the variant border so it stays visible on any stage
    // background. `border-1` matches the variant border width to avoid a layout shift on toggle.
    // Use a softened primary (the theme's `primary` is near-black, which reads as a harsh edge at
    // full strength) so the selected outline stays gentle.
    isSelected && "border-1 border-solid border-primary/40",
    // Authoring aid: fade tiles whose grapheme file isn't version-complete yet, so unfinished
    // mnemonics stand out. Gated on the device setting; the completion fact is batched by the caller.
    fadeMissingMnemonicEnabled && isMissingMnemonic && "opacity-25",
    className,
  );
  const resolvedOnPress = onPress === undefined ? undefined : handlePress;
  const morpheme =
    morphemeGlyphTileSlice.morphemeType === "grapheme"
      ? morphemeGlyphTileSlice.grapheme
      : morphemeGlyphTileSlice.characters.map(({ grapheme }) => grapheme).join("");
  const testID = getMorphemeTileTestID(morpheme);
  const accessibilityStage = stage === "unadded" ? "not added" : stage;
  const reading =
    showPrimaryPinyin && morphemeGlyphTileSlice.morphemeType === "grapheme"
      ? morphemeGlyphTileSlice.primaryPinyin
      : null;
  const accessibilityLabel = `${morpheme}${reading ? `, ${reading}` : ""}, ${accessibilityStage} stage`;
  return (
    <Button
      accessibilityLabel={accessibilityLabel}
      className={resolvedClassName}
      delayLongPress={MORPHEME_TILE_DELAY_LONG_PRESS_MS}
      isLoading={isLoading}
      onLongPress={onLongPress}
      onPress={resolvedOnPress}
      size="fill"
      testID={testID}
      variant="outline"
    >
      <MorphemeGlyphTileContent
        front={front}
        glyphStyle={glyphStyle}
        graphemeGlyph={graphemeGlyph}
        morphemeGlyphTileSlice={morphemeGlyphTileSlice}
        showPrimaryPinyin={showPrimaryPinyin}
        uncolored={uncolored}
      />
    </Button>
  );
}

export function MorphemeGlyphTileUi(props: MorphemeGlyphTileUiProps) {
  return (
    <MorphemeTilePresentationProvider>
      <MorphemeGlyphTileUiContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}
